import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  eventsAPI, quotesAPI, galleryAPI,
  announcementsAPI, radioAPI,
} from '../../utils/api';
import {
  CalendarIcon, PhotoIcon, StarIcon,
  MegaphoneIcon, RadioIcon, UsersIcon,
} from '@heroicons/react/24/outline';

export default function DashboardPage() {
  const { user, can } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    events: 0, quotes: 0, gallery: 0, announcements: 0,
  });

  useEffect(() => {
    Promise.allSettled([
      eventsAPI.getAll({ limit: 1, status: 'published' }),
      quotesAPI.getAll(),
      galleryAPI.getItems({ limit: 1 }),
      announcementsAPI.getAll(),
    ]).then(([e, q, g, an]) => {
      setStats({
        events:        e.status  === 'fulfilled' ? (e.value.data?.total  || e.value.data?.data?.length  || 0) : 0,
        quotes:        q.status  === 'fulfilled' ? (q.value.data?.data?.length  || 0) : 0,
        gallery:       g.status  === 'fulfilled' ? (g.value.data?.data?.length  || 0) : 0,
        announcements: an.status === 'fulfilled' ? (an.value.data?.data?.length || 0) : 0,
      });
    });
  }, []);

  // Stat cards — no Azkar
  const statCards = [
    { label: 'Events',        value: stats.events,        icon: CalendarIcon,  to: '/admin/events',        module: 'events',        color: 'bg-blue-50 text-blue-600'   },
    { label: 'Quotes',        value: stats.quotes,        icon: StarIcon,       to: '/admin/quotes',        module: 'quotes',        color: 'bg-amber-50 text-amber-600' },
    { label: 'Gallery Items', value: stats.gallery,       icon: PhotoIcon,      to: '/admin/gallery',       module: 'gallery',       color: 'bg-purple-50 text-purple-600'},
    { label: 'Announcements', value: stats.announcements, icon: MegaphoneIcon,  to: '/admin/announcements', module: 'announcements', color: 'bg-red-50 text-red-600'     },
  ].filter(s => {
    if (typeof can !== 'function') return true; // safe fallback
    return can(s.module);
  });

  // Quick actions — no Azkar, all routes exist
  const quickLinks = [
    {
      to: '/admin/events/new',
      label: 'New Event',
      icon: '📅',
      desc: 'Create a new event or programme',
      module: 'events',
    },
    {
      to: '/admin/quotes',
      label: 'New Quote',
      icon: '🌙',
      desc: 'Add a Murshid quote or wisdom',
      module: 'quotes',
    },
    {
      to: '/admin/gallery',
      label: 'Upload Media',
      icon: '🖼',
      desc: 'Upload photos, videos or documents',
      module: 'gallery',
    },
    {
      to: '/admin/announcements',
      label: 'New Announcement',
      icon: '📢',
      desc: 'Post an announcement for members',
      module: 'announcements',
    },
    {
      to: '/admin/radio',
      label: 'Update Radio',
      icon: '📻',
      desc: 'Update JNF Radio stream settings',
      module: 'radio',
    },
    {
      to: '/admin/members',
      label: 'View Members',
      icon: '👥',
      desc: 'Manage member roles and profiles',
      module: 'members',
    },
  ].filter(l => {
    if (typeof can !== 'function') return true;
    return can(l.module);
  });

  return (
    <AdminLayout>
      {/* Welcome */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-navy-800">
          Assalamu Alaykum, {user?.full_name?.split(' ')[0]} 🌙
        </h1>
        <p className="text-gray-500 text-sm mt-1 capitalize">
          Logged in as{' '}
          <span className="font-semibold text-navy-600">
            {user?.role?.replace(/_/g, ' ')}
          </span>
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map(({ label, value, icon: Icon, to, color }) => (
          <Link
            key={to} to={to}
            className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 group"
          >
            <div className={`inline-flex p-2.5 rounded-xl ${color} mb-3`}>
              <Icon className="h-5 w-5" />
            </div>
            <p className="text-3xl font-bold text-navy-800">{value}</p>
            <p className="text-xs text-gray-500 mt-1 group-hover:text-navy-600 transition-colors">
              {label}
            </p>
          </Link>
        ))}
      </div>

      {/* Quick actions */}
      {quickLinks.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm mb-6">
          <h2 className="font-semibold text-navy-700 mb-4">⚡ Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {quickLinks.map(({ to, label, icon, desc }) => (
              <Link
                key={to}
                to={to}
                className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 hover:bg-navy-50 border border-transparent hover:border-navy-100 transition-all group"
              >
                <span className="text-xl flex-shrink-0 mt-0.5">{icon}</span>
                <div>
                  <p className="font-semibold text-navy-700 text-sm group-hover:text-navy-900">
                    {label}
                  </p>
                  <p className="text-gray-400 text-xs mt-0.5">{desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Getting Started tips — Azkar removed */}
      <div className="bg-gradient-to-br from-cyan-50 to-navy-50 border border-cyan-100 rounded-2xl p-6">
        <p className="text-cyan-700 font-semibold mb-3 text-sm">📖 Getting Started</p>
        <ul className="space-y-2">
          {[
            { icon: '📅', text: 'Create your first event from Events → New Event' },
            { icon: '📻', text: 'Set your radio stream URL in Radio (Online) settings' },
            { icon: '🕌', text: 'Add your Sawia locations so members can find you' },
            { icon: '📡', text: 'Add terrestrial radio stations under Radio Stations' },
            { icon: '👥', text: 'Assign membership roles to registered members' },
            { icon: '📢', text: 'Pin important announcements so they appear prominently' },
          ].map(({ icon, text }) => (
            <li key={text} className="flex items-start gap-2 text-sm text-cyan-700">
              <span className="flex-shrink-0">{icon}</span>
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </div>
    </AdminLayout>
  );
}
